import React from 'react';
import { Lock } from 'lucide-react';
import { IntegrationLogo } from '../IntegrationLogo';
import { EXPORT_LABEL, EXPORT_META, EXPORT_TITLE, ExportPanel, PanelHeader } from './exportUi';

/**
 * "BUILT FOR TEAMS", AS PRODUCT UI — AN EXPORT-ONLY ASSET.
 *
 * WHY THIS EXISTS. The home page's "Built for teams" card carries an
 * illustration of a group of people. That works as decoration inside a
 * marketing board, but the export page is an asset library: its users are
 * teammates and developers pulling frames into videos, decks and docs. An
 * illustration is the one asset nobody can use that way — you cannot point at
 * it and say "this is the thing". Every other frame on that page is real
 * product UI, so this is the odd one out.
 *
 * So this is the same claim — "shared access, workflows and company context in
 * one place, not a drawer of personal accounts" — stated as the screen that
 * proves it: a group, the people in it, the access each one has, and the
 * connections the whole group reaches through. The last row is the argument:
 * the tools belong to the GROUP, so nobody is holding them in a personal
 * login.
 *
 * It deliberately does NOT replace the home page card. The two are allowed to
 * differ: the page is selling a benefit, the asset is showing a product.
 *
 * Fully static by design — no hover, no entrance, no interval. An asset that
 * only looks right after an animation has run is an asset that exports wrong.
 */

/** One group, six people. Access is a real distinction in the product — an
 * owner administers the group, members use what it holds, and a viewer can see
 * outputs without being able to run anything. Showing all three is what makes
 * this read as governance rather than a contact list. */
const MEMBERS: {
  name: string;
  role: string;
  access: 'Owner' | 'Member' | 'Viewer';
  initials: string;
}[] = [{
  name: 'Sarah Chen',
  role: 'VP Revenue',
  access: 'Owner',
  initials: 'SC'
}, {
  name: 'Marcus Webb',
  role: 'Account Executive',
  access: 'Member',
  initials: 'MW'
}, {
  name: 'Priya Raman',
  role: 'Sales Engineer',
  access: 'Member',
  initials: 'PR'
}, {
  name: 'Tom Alvarez',
  role: 'RevOps',
  access: 'Member',
  initials: 'TA'
}, {
  name: 'Dana Okafor',
  role: 'Finance',
  access: 'Viewer',
  initials: 'DO'
}];

/** A relatable revenue workflow: Salesforce holds the pipeline, Gong holds
 * customer calls, Gmail holds direct communication, and Slack coordinates the
 * team. Together these explain why shared access matters better than a generic
 * productivity-tool mix. */
const GROUP_CONNECTIONS: [string, string][] = [['salesforce', 'Salesforce'], ['gong', 'Gong'], ['gmail', 'Gmail'], ['slack', 'Slack']];

/** Owner gets the accent, viewer gets the quietest treatment — the chip is a
 * scanning aid, so the three levels must be distinguishable at a glance rather
 * than three shades of the same warm grey. */
const ACCESS_STYLE: Record<string, string> = {
  Owner: 'bg-[#FDF0E6] text-[#B4470F]',
  Member: 'bg-[#F1EAE0] text-[#6B5D52]',
  Viewer: 'border border-[#EFE7DA] text-[#8A7B6D]'
};
export function SharedAccessPanel() {
  return <ExportPanel data-id="element-3416" data-mp="y17qesy0q366">
      {/* The group name and role chips already explain the access model. A
       generic "shared" badge has no clear object here, so the header stays
       focused on the team and its contents. */}
      <PanelHeader label="Group" title="Revenue team" meta="5 people · 4 connections" data-id="element-3417" data-mp="ozzswd3bt6d5" />

      {/* People and their access. */}
      <ul className="px-6 py-1" data-id="element-3418" data-mp="1gticjb4gdmhl">
        {MEMBERS.map(member => <li key={member.name} className="flex items-center gap-3 border-b border-[#F1EAE0] py-3.5 last:border-0" data-id="element-3419" data-mp="1gu1uusy0mcw5">
            <span aria-hidden="true" className={`flex size-8 flex-none items-center justify-center rounded-full text-[11.5px] font-bold ${member.access === 'Owner' ? 'bg-[#EA580C] text-white' : 'bg-[#F1EAE0] text-[#6B5D52]'}`} data-id="element-3420" data-mp="1ctrmxaqnxcen">
              {member.initials}
            </span>
            <span className="min-w-0 flex-1" data-id="element-3421" data-mp="3sh8acvu5647c">
              <span className={`${EXPORT_TITLE} block truncate`} data-id="element-3422" data-mp="1kad4pgs85db5">{member.name}</span>
              <span className={`${EXPORT_META} block truncate`} data-id="element-3423" data-mp="1p0thqzs4uwwz">{member.role}</span>
            </span>
            <span className={`flex-none rounded-full px-2.5 py-1 text-[11.5px] font-bold ${ACCESS_STYLE[member.access]}`} data-id="element-3424" data-mp="1b9f12zlp77t3">
              {member.access}
            </span>
          </li>)}
      </ul>

      {/* Connections available to this group. A stacked footer gives the label
       and tool chips their own rhythm instead of squeezing both into one row. */}
      <div className="mt-1 border-t border-[#EFE7DA] bg-[#FAF8F4] px-6 py-4" data-id="element-3425" data-mp="27z5v6xq2usyd">
        <p className={`${EXPORT_LABEL} flex items-center gap-2`} data-id="element-3426" data-mp="1tyv35rd178bm">
          <Lock size={12} strokeWidth={2.4} data-id="element-3427" data-mp="1yyr75qlqazr6" />
          Connections
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2.5" data-id="element-3428" data-mp="1z0u7b3gli99m">
          {GROUP_CONNECTIONS.map(([slug, name]) => <span key={slug} className="flex h-8 items-center gap-2 rounded-full border border-[#E7DED1] bg-white pl-1.5 pr-3.5" data-id="element-3429" data-mp="njugkc9wc2hp">
              <IntegrationLogo slug={slug} name={name} className="size-[19px]" rounded="rounded-full" data-id="element-3430" data-mp="f226on8bc3gu" />
              <span className="whitespace-nowrap text-[12px] font-semibold text-[#2B211A]" data-id="element-3431" data-mp="b06vmr0kybpv">{name}</span>
            </span>)}
        </div>
      </div>
    </ExportPanel>;
}